import Flickity from 'flickity';

import {register} from '../vendor/theme-scripts/theme-sections';
import {videoPlay} from '../features/video-play';
import flickitySmoothScrolling from '../globals/flickity-smooth-scrolling';
import throttle from '../util/throttle';

const selectors = {
  arrowScrollDown: '[data-scroll-down]',
  header: '[data-site-header]',
  item: '[data-slide]',
  links: 'a, button',
  main: '[data-main]',
  slider: '[data-slider]',
};

const attributes = {
  style: 'data-style',
  currentStyle: 'data-current-style',
  tabIndex: 'tabindex',
  slidePosition: 'data-slide-position',
};

const classes = {
  headerFixed: 'site-header--fixed',
};

const sections = {};

class Slider {
  constructor(section) {
    this.container = section.container;
    this.header = document.querySelector(selectors.header);
    this.flkty = null;

    this.initSlider();
    this.initZoomAnimation();
    this.bindScrollButton();
  }

  initSlider() {
    const slidesCount = this.container.querySelectorAll(selectors.item).length;
    const duration = parseInt(this.container.dataset.duration);
    const pageDots = this.container.dataset.pageDots === 'true' && slidesCount > 1;
    const prevNextButtons = this.container.dataset.navArrows === 'true' && slidesCount > 1;
    const fade = this.container.dataset.transition !== 'slide';
    let autoplay = this.container.dataset.autoplay === 'true';

    if (autoplay) {
      autoplay = duration;
    }

    if (slidesCount > 1) {
      this.flkty = new Flickity(this.container, {
        fade: fade,
        cellSelector: selectors.item,
        autoPlay: autoplay,
        wrapAround: true,
        adaptiveHeight: true,
        setGallerySize: true,
        imagesLoaded: true,
        pageDots: pageDots,
        prevNextButtons: prevNextButtons,
        on: {
          ready: () => {
            const currentStyle = this.container.querySelector(`${selectors.item}[${attributes.slidePosition}="1"]`).getAttribute(attributes.style);
            this.container.setAttribute(attributes.currentStyle, currentStyle);
          },
          change: (index) => {
            const currentSlide = this.flkty.selectedElement;
            const currentStyle = currentSlide.getAttribute(attributes.style);

            this.container.setAttribute(attributes.currentStyle, currentStyle);

            this.flkty.cells.forEach((slide, i) => {
              slide.element.querySelectorAll(selectors.links).forEach((link) => {
                link.setAttribute(attributes.tabIndex, i === index ? '0' : '-1');
              });
            });
          },
        },
      });

      // iOS smooth scrolling fix
      flickitySmoothScrolling(this.container);
    } else if (slidesCount === 1) {
      const currentStyle = this.container.querySelector(selectors.item).getAttribute(attributes.style);
      this.container.setAttribute(attributes.currentStyle, currentStyle);
    }
  }

  // Parallax effect to zoom image on scroll
  initZoomAnimation() {
    if (this.container.dataset.zoomAnimation !== 'true') {
      return;
    }

    // Target element to be observed.
    const observedElement = this.container;
    const firstSection = document.body.querySelector(selectors.main).children[0];
    const isFirstSection = this.container.parentNode === firstSection;
    const hasTransparentHeader = this.header.dataset.transparent == 'true';

    const renderZoomEffect = () => {
      const headerHeight = isFirstSection & hasTransparentHeader ? 0 : parseInt(this.header.dataset.height || this.header.offsetHeight);
      const rect = observedElement.getBoundingClientRect();
      const sectionHeight = observedElement.offsetHeight;
      const scrolled = isFirstSection ? headerHeight - rect.top : headerHeight - rect.top + window.innerHeight;
      const scrolledPercentage = scrolled / sectionHeight;
      let transitionSpeed = 0.1; // Set value between 0 and 1. Bigger value will make the zoom more aggressive
      if (isFirstSection) {
        transitionSpeed *= 1.5;
      }

      let scale = 1 + scrolledPercentage * transitionSpeed;

      // Prevent image scale down under 100%
      scale = scale > 1 ? scale : 1;
      observedElement.style.setProperty('--scale', scale);
    };

    renderZoomEffect();

    this.zoomOnScrollEvent = throttle(renderZoomEffect, 5);

    // Intersection Observer Configuration
    const observerOptions = {
      root: null,
      rootMargin: '0px', // important: needs units on all values
      threshold: 0,
    };

    // Intersection Observer Callback Function
    const intersectionCallback = (entry) => {
      if (entry[0].isIntersecting) {
        window.addEventListener('scroll', this.zoomOnScrollEvent);
      } else {
        window.removeEventListener('scroll', this.zoomOnScrollEvent);
      }
    };

    // Intersection Observer Constructor.
    const observer = new IntersectionObserver(intersectionCallback, observerOptions);

    observer.observe(observedElement);
  }

  // Scroll down function
  bindScrollButton() {
    const arrowDown = this.container.querySelector(selectors.arrowScrollDown);

    if (arrowDown) {
      arrowDown.addEventListener('click', (e) => {
        e.preventDefault();

        const headerHeight = this.header.classList.contains(classes.headerFixed) ? 60 : 0;
        const scrollToPosition = parseInt(Math.ceil(this.container.offsetTop + this.container.offsetHeight - headerHeight));

        window.scrollTo({
          top: scrollToPosition,
          left: 0,
          behavior: 'smooth',
        });
      });
    }
  }

  onBlockSelect(evt) {
    const index = parseInt([...evt.target.parentNode.children].indexOf(evt.target));

    if (this.flkty !== null) {
      this.flkty.select(index);
      this.flkty.pausePlayer();
    }
  }

  onBlockDeselect(evt) {
    const autoplay = evt.target.closest(selectors.slider).dataset.autoplay;
    if (autoplay && this.flkty !== null) {
      this.flkty.playPlayer();
    }
  }

  onUnload() {
    if (this.flkty !== null) {
      this.flkty.destroy();
      this.flkty = null;
    }

    if (this.zoomOnScrollEvent !== null) {
      window.removeEventListener('scroll', this.zoomOnScrollEvent);
    }
  }
}

const slider = {
  onLoad() {
    sections[this.id] = new Slider(this);
  },
  onUnload(e) {
    sections[this.id].onUnload(e);
  },
  onBlockSelect(e) {
    sections[this.id].onBlockSelect(e);
  },
  onBlockDeselect(e) {
    sections[this.id].onBlockDeselect(e);
  },
};

register('slider', [slider, videoPlay]);
